import { useEffect, useState } from 'react';
import { supabase } from './supabaseClient';
import NavDrawer from './NavDrawer';

const C = {
  gold: 'var(--gold)',
  cyan: 'var(--cyan)',
  green: 'var(--green)',
  red: 'var(--red-2)',
  bg: 'var(--bg)',
  card: 'var(--panel)',
  line: 'rgba(var(--cyan-rgb),0.14)',
  lineSoft: 'rgba(var(--wash-rgb),0.07)',
  ink: 'var(--text)',
  inkDim: 'var(--muted-5)',
  inkFaint: '#5c6a80',
};

const DISPLAY = "'Orbitron', monospace";
const BODY = "'Rajdhani', sans-serif";
const MONO = "'Share Tech Mono', monospace";

const CONTACT = 'keeper18@collectionkeeper.org';

const RESPONSIVE_CSS = `
.ai-page {
  min-height: 100vh;
  background: ${C.bg};
  font-family: ${BODY};
  font-size: 17px;
  line-height: 1.55;
  color: ${C.ink};
  -webkit-font-smoothing: antialiased;
  position: relative;
}
.ai-page::before {
  content: "";
  position: fixed; inset: 0; pointer-events: none; z-index: 0;
  background:
    radial-gradient(900px 500px at 12% -8%, rgba(var(--cyan-rgb),.07), transparent 60%),
    radial-gradient(700px 420px at 92% 4%, rgba(var(--gold-rgb),.05), transparent 60%);
}
.ai-wrap { position: relative; z-index: 1; max-width: 1180px; margin: 0 auto; padding: 0 clamp(16px, 4vw, 40px) 90px; }
.ai-clear-nav { padding-right: 56px; }
.ai-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1px; margin-top: 30px; background: ${C.lineSoft}; border: 1px solid ${C.lineSoft}; max-width: 560px; }
.ai-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(214px, 1fr)); gap: 16px; margin-top: 26px; }
.ai-tile:hover { border-color: ${C.cyan} !important; transform: translateY(-3px); }
@media (max-width: 560px) {
  .ai-stats { grid-template-columns: repeat(3, 1fr); }
}
`;

function CardFrame({ children }) {
  return (
    <div style={{
      aspectRatio: '2.5 / 3.5', background: C.card, borderBottom: `1px solid ${C.lineSoft}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 16,
    }}>
      <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.14em', color: C.inkFaint, lineHeight: 1.8, textTransform: 'uppercase' }}>
        {children}
      </div>
    </div>
  );
}

function fmtApprox(n) {
  if (!n) return '';
  return `~${(Math.round(n / 50) * 50).toLocaleString('en-US')}`;
}

export default function ArtistIndex() {
  const [user, setUser] = useState(null);
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => setUser(user));
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const { data, error } = await supabase
        .from('artists')
        .select('slug, name, city, country, portrait_url, status, artist_sets(card_count_stated, contribution_type)')
        .eq('status', 'published')
        .order('name');
      if (cancelled) return;
      if (!error && data) {
        setArtists(data.map(a => {
          // Illustrated insert sets are printed runs, not sketch work — they
          // never count toward an artist's sketch sets or cards drawn.
          const sketchSets = (a.artist_sets || []).filter(s => s.contribution_type !== 'illustrated');
          return {
            slug: a.slug,
            name: a.name,
            location: [a.city, a.country].filter(Boolean).join(', '),
            portraitUrl: a.portrait_url,
            setCount: sketchSets.length,
            cardCount: sketchSets.reduce((sum, s) => sum + (s.card_count_stated || 0), 0),
          };
        }));
      }
      setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const setsCovered = artists.reduce((sum, a) => sum + a.setCount, 0);

  return (
    <div className="ai-page">
      <style>{RESPONSIVE_CSS}</style>
      {user && <NavDrawer user={user} />}
      <div className="ai-wrap">

        <div style={{ padding: '52px 0 30px' }}>
          <div className="ai-clear-nav" style={{ fontFamily: MONO, fontSize: 11, letterSpacing: '.22em', textTransform: 'uppercase', color: C.inkFaint }}>
            <a href="/resources" style={{ color: C.inkFaint, textDecoration: 'none' }}>Resources</a>
            {' / '}Sketch artists
          </div>
          <h1 style={{ fontFamily: DISPLAY, textTransform: 'uppercase', fontSize: 'clamp(28px, 5vw, 48px)', fontWeight: 900, color: '#fff', margin: '10px 0 0' }}>
            Sketch artists
          </h1>
          <p style={{ fontSize: 19, color: C.inkDim, margin: '14px 0 0', maxWidth: '52ch' }}>
            The people who draw the one-of-a-kind cards hiding in Star Wars packs. We are writing one profile at a time and checking every claim before it goes up.
          </p>
          <div className="ai-stats">
            {[
              { num: String(artists.length), label: 'Profiles published' },
              { num: fmtApprox(artists.reduce((sum, a) => sum + a.cardCount, 0)) || '0', label: 'Cards documented' },
              { num: String(setsCovered), label: 'Sets covered' },
            ].map((s, i) => (
              <div key={i} style={{ background: C.card, padding: '14px 16px' }}>
                <b style={{ display: 'block', fontFamily: MONO, fontSize: 'clamp(19px, 3vw, 28px)', color: C.cyan, lineHeight: 1.1, fontWeight: 400 }}>
                  {loading ? '…' : s.num}
                </b>
                <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.14em', textTransform: 'uppercase', color: C.inkFaint }}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="ai-grid">
          {artists.map(a => (
            <a key={a.slug} href={`/resources/artists/${a.slug}`} style={{ textDecoration: 'none' }}>
              <article className="ai-tile" style={{ background: C.card, border: `1px solid ${C.lineSoft}`, borderRadius: 3, overflow: 'hidden', transition: '.18s', cursor: 'pointer' }}>
                {a.portraitUrl ? (
                  <div style={{ aspectRatio: '2.5 / 3.5', overflow: 'hidden', borderBottom: `1px solid ${C.lineSoft}` }}>
                    <img src={a.portraitUrl} alt={a.name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  </div>
                ) : (
                  <CardFrame>Artist photo<br />coming with permission</CardFrame>
                )}
                <div style={{ padding: '13px 14px 15px' }}>
                  <h4 style={{ fontFamily: DISPLAY, fontSize: 14.5, fontWeight: 700, color: '#fff', textTransform: 'uppercase', margin: '0 0 4px', lineHeight: 1.2 }}>{a.name}</h4>
                  <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', color: C.inkFaint }}>{a.location}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 11, paddingTop: 9, borderTop: `1px solid ${C.lineSoft}` }}>
                    <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.08em', color: C.inkDim }}>
                      {a.setCount} sets{a.cardCount ? ` · ${fmtApprox(a.cardCount)} cards` : ''}
                    </span>
                    <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.08em', color: C.inkDim }}>
                      <i style={{ width: 7, height: 7, borderRadius: '50%', display: 'inline-block', marginRight: 5, verticalAlign: 'middle', background: C.green }} />
                      Published
                    </span>
                  </div>
                </div>
              </article>
            </a>
          ))}

          {/* Honest placeholder — never a fake artist name */}
          <article style={{ background: C.card, border: `1px dashed ${C.lineSoft}`, borderRadius: 3, overflow: 'hidden', opacity: 0.55 }}>
            <CardFrame>More profiles<br />coming</CardFrame>
            <div style={{ padding: '13px 14px 15px' }}>
              <h4 style={{ fontFamily: DISPLAY, fontSize: 14.5, fontWeight: 700, color: '#fff', textTransform: 'uppercase', margin: '0 0 4px', lineHeight: 1.2 }}>More profiles coming</h4>
              <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', color: C.inkFaint }}>One at a time</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 11, paddingTop: 9, borderTop: `1px solid ${C.lineSoft}` }}>
                <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.08em', color: C.inkDim }}>Every claim checked first</span>
                <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.08em', color: C.inkDim }}>
                  <i style={{ width: 7, height: 7, borderRadius: '50%', display: 'inline-block', marginRight: 5, verticalAlign: 'middle', background: C.gold }} />
                  In progress
                </span>
              </div>
            </div>
          </article>

          {/* Suggest an artist */}
          <a href={`mailto:${CONTACT}?subject=${encodeURIComponent('Sketch artist suggestion')}`} style={{ textDecoration: 'none' }}>
            <article className="ai-tile" style={{ background: C.card, border: `1px dashed ${C.line}`, borderRadius: 3, overflow: 'hidden', transition: '.18s', cursor: 'pointer' }}>
              <CardFrame>Suggest an artist</CardFrame>
              <div style={{ padding: '13px 14px 15px' }}>
                <h4 style={{ fontFamily: DISPLAY, fontSize: 14.5, fontWeight: 700, color: '#fff', textTransform: 'uppercase', margin: '0 0 4px', lineHeight: 1.2 }}>Who should be next</h4>
                <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', color: C.inkFaint }}>Tell us</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 11, paddingTop: 9, borderTop: `1px solid ${C.lineSoft}` }}>
                  <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.08em', color: C.inkDim }}>Collector suggestions open</span>
                  <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.08em', color: C.inkDim }}>
                    <i style={{ width: 7, height: 7, borderRadius: '50%', display: 'inline-block', marginRight: 5, verticalAlign: 'middle', background: C.cyan }} />
                    Open
                  </span>
                </div>
              </div>
            </article>
          </a>
        </div>

        {/* Why the colored spine — public explanation of the provenance rail */}
        <section style={{ position: 'relative', padding: '44px 0 20px', paddingLeft: 'clamp(20px, 4vw, 40px)', marginTop: 20 }}>
          <div aria-hidden="true" style={{ position: 'absolute', left: 0, top: 46, bottom: 14, width: 3, borderRadius: 2, background: C.green, opacity: 0.85 }} />
          <div className="ai-clear-nav" style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap', marginBottom: 6 }}>
            <h2 style={{ fontFamily: DISPLAY, textTransform: 'uppercase', fontSize: 'clamp(18px, 2.6vw, 25px)', fontWeight: 700, color: '#fff', margin: 0 }}>
              Why the colored spine
            </h2>
          </div>
          <p style={{ maxWidth: '70ch' }}>
            Every profile carries a rail down its left edge. Green means we confirmed it across independent sources. Gold means we found it in one place and you should check it yourself before relying on it. Red means we could not confirm it at all and we are saying so out loud rather than filling the gap with a guess.
          </p>
        </section>

      </div>
    </div>
  );
}
